import { useState, useEffect, useRef } from 'react';
import agentActions from '../../../../actions/agent';

/**
 * Polls for background auto-analysis results after image generation.
 * Returns analysis data and cleanup when thread changes.
 */
export default function useAnalysisPolling(threadId, reportedJobIdsRef, messagesLength) {
  const [analysisResults, setAnalysisResults] = useState(null);
  const [hasUnanalyzedJobs, setHasUnanalyzedJobs] = useState(false);
  const analysisPollingRef = useRef(null);
  const analyzedJobIdsRef = useRef(new Set());

  // Detect new unanalyzed jobs from reported IDs
  useEffect(() => {
    const unanalyzed = [...reportedJobIdsRef.current].filter(id => !analyzedJobIdsRef.current.has(id));
    if (unanalyzed.length > 0) setHasUnanalyzedJobs(true);
  }, [messagesLength]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for background auto-analysis results
  useEffect(() => {
    if (!threadId || !hasUnanalyzedJobs) return;

    const poll = async () => {
      try {
        const res = await agentActions.getThreadAnalysis(threadId);
        const analysis = res?.data;
        if (!analysis || analysis.status === 'polling' || analysis.status === 'analyzing') return;

        if ((analysis.status === 'complete' || analysis.status === 'consumed') && analysis.results?.length > 0) {
          for (const id of analysis.jobIds || []) analyzedJobIdsRef.current.add(id);
          setAnalysisResults(analysis);
          setHasUnanalyzedJobs(false);
          if (analysisPollingRef.current) {
            clearInterval(analysisPollingRef.current);
            analysisPollingRef.current = null;
          }
        } else if (analysis.status === 'failed') {
          setHasUnanalyzedJobs(false);
          if (analysisPollingRef.current) {
            clearInterval(analysisPollingRef.current);
            analysisPollingRef.current = null;
          }
        }
      } catch (err) {
        console.warn('[Analysis poll error]', err);
      }
    };

    const initTimer = setTimeout(() => {
      poll();
      analysisPollingRef.current = setInterval(poll, 5000);
    }, 10000);

    return () => {
      clearTimeout(initTimer);
      if (analysisPollingRef.current) {
        clearInterval(analysisPollingRef.current);
        analysisPollingRef.current = null;
      }
    };
  }, [threadId, hasUnanalyzedJobs]);

  // Clean up on thread change
  useEffect(() => {
    setAnalysisResults(null);
    setHasUnanalyzedJobs(false);
    analyzedJobIdsRef.current.clear();
    if (analysisPollingRef.current) {
      clearInterval(analysisPollingRef.current);
      analysisPollingRef.current = null;
    }
  }, [threadId]);

  return { analysisResults, setAnalysisResults };
}
